import { Component, OnInit } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
interface FieldOption {
  label: string;
  value: string;
}

export interface ModifiedFields {
  id?: number;
  modifiedAdoptionDate?: boolean;
  modifiedAmendmentDate?: boolean;
  modifiedEffectiveDate?: boolean;
  modifiedExpirationDate?: boolean;
  modifiedNumberOfShares?: boolean;
  modifiedPlanAction?: boolean;
  modifiedTerminationDate?: boolean;
  modifiedTransactionType?: boolean;
  tradingPlanId?: number;
  modifiedOther?: boolean;
}

@Component({
  selector: "app-add-text",
  templateUrl: "./add-text.component.html",
  styleUrl: "./add-text.component.css",
  standalone: false,
})
export class AddTextComponent implements OnInit {
  selectedFields: string[] = [];
  comment: string = "";
  isEstimatedAdoptionDate = false;
  isEstimatedTerminationDate = false;
  isEstimatedAmendedDate = false;

  processed = false;
  processedEdit = false;

  readonly fieldOptions: FieldOption[] = [
    { label: "Plan Action", value: "plan_action" },
    { label: "Adoption Date", value: "adoption_date" },
    { label: "Effective Date", value: "effective_date" },
    { label: "Amendment Date", value: "amendment_date" },
    { label: "Termination Date", value: "termination_date" },
    { label: "Expiration Date", value: "expiration_date" },
    { label: "Number of Shares", value: "number_of_shares" },
    { label: "Transaction Type", value: "transaction_type" },
    { label: "Other", value: "modifiedOther" },
  ];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {}

  ngOnInit(): void {
    this.processed = !!this.config.data?.processed;
    this.processedEdit = !!this.config.data?.processedEdit;

    const existing = this.config.data;
    if (existing) {
      // Check if data is in new format (from writeText function)
      if (
        existing.modificationComment !== undefined ||
        existing.modifiedFields !== undefined ||
        existing.isEstimatedAdoptionDate !== undefined ||
        existing.isEstimatedTerminationDate !== undefined ||
        existing.tradingPlanId !== undefined
      ) {
        this.applyNewFormat(existing);
        return;
      }
      // Otherwise, use existing logic
      this.applyExisting(existing);
    }
  }

  get disableSave(): boolean {
    const hasSelectedFields = this.selectedFields.length > 0;
    const hasComment = this.comment?.trim().length > 0;
    return this.processed || !hasSelectedFields || !hasComment;
  }

  get isAdoptionSelected(): boolean {
    return this.selectedFields.includes("adoption_date");
  }

  get isTerminationSelected(): boolean {
    return this.selectedFields.includes("termination_date");
  }
 get isAmendmentSelected(): boolean {
    return this.selectedFields.includes("amendment_date");
  }
  onCancel(): void {
    this.ref.close();
  }

  onAdd(): void {
    if (!this.isAdoptionSelected) {
      this.isEstimatedAdoptionDate = false;
    }
    if (!this.isTerminationSelected) {
      this.isEstimatedTerminationDate = false;
    }
     if (!this.isAmendmentSelected) {
      this.isEstimatedAmendedDate = false;
    }

    // const commentValue =
    //   this.selectedFields.length > 0 ? this.comment?.trim() ?? "" : "";

    const commentValue = this.comment?.trim() ?? "";

    // Get original modifiedFields if available (to preserve id, tradingPlanId, etc.)
    const originalModifiedFields = this.config.data?.modifiedFields;
    // Priority: config.data.tradingPlanId > modifiedFields.tradingPlanId
    const tradingPlanIdFromData =
      this.config.data?.tradingPlanId ?? originalModifiedFields?.tradingPlanId;

    const modifiedFields: ModifiedFields = {
      ...(originalModifiedFields?.id !== undefined && {
        id: originalModifiedFields.id,
      }),
      ...(tradingPlanIdFromData !== undefined && {
        tradingPlanId: tradingPlanIdFromData,
      }),
      modifiedPlanAction: false,
      modifiedAdoptionDate: false,
      modifiedEffectiveDate: false,
      modifiedAmendmentDate: false,
      modifiedTerminationDate: false,
      modifiedExpirationDate: false,
      modifiedNumberOfShares: false,
      modifiedTransactionType: false,
      modifiedOther: false,
    };

    this.selectedFields.forEach((field) => {
      switch (field) {
        case "plan_action":
          modifiedFields.modifiedPlanAction = true;
          break;
        case "adoption_date":
          modifiedFields.modifiedAdoptionDate = true;
          break;
        case "effective_date":
          modifiedFields.modifiedEffectiveDate = true;
          break;
        case "amendment_date":
          modifiedFields.modifiedAmendmentDate = true;
          break;
        case "termination_date":
          modifiedFields.modifiedTerminationDate = true;
          break;
        case "expiration_date":
          modifiedFields.modifiedExpirationDate = true;
          break;
        case "number_of_shares":
          modifiedFields.modifiedNumberOfShares = true;
          break;
        case "transaction_type":
          modifiedFields.modifiedTransactionType = true;
          break;
        case "modifiedOther":
          modifiedFields.modifiedOther = true;
          break;
      }
    });

    const payload = {
      modifiedFields,
      comment: commentValue,
      is_estimated_adoption_date:
        this.isAdoptionSelected && this.isEstimatedAdoptionDate,
      is_estimated_termination_date:
        this.isTerminationSelected && this.isEstimatedTerminationDate,
      is_estimated_amended_date:
        this.isAmendmentSelected && this.isEstimatedAmendedDate,
    };

    this.ref.close(JSON.stringify(payload));
  }

  private applyNewFormat(data: any): void {
    // Set comment from modificationComment
    if (data.modificationComment !== undefined) {
      this.comment = data.modificationComment || "";
    }

    // Set selectedFields from modifiedFields object
    if (data.modifiedFields && typeof data.modifiedFields === "object") {
      const mapping: Record<string, keyof ModifiedFields> = {
        plan_action: "modifiedPlanAction",
        adoption_date: "modifiedAdoptionDate",
        effective_date: "modifiedEffectiveDate",
        amendment_date: "modifiedAmendmentDate",
        termination_date: "modifiedTerminationDate",
        expiration_date: "modifiedExpirationDate",
        number_of_shares: "modifiedNumberOfShares",
        transaction_type: "modifiedTransactionType",
        modifiedOther: "modifiedOther",
      };

      this.selectedFields = Object.entries(mapping)
        .filter(([_, prop]) => data.modifiedFields[prop])
        .map(([field]) => field);
    }

    // Set isEstimatedAdoptionDate
    if (data.isEstimatedAdoptionDate !== undefined) {
      this.isEstimatedAdoptionDate = !!data.isEstimatedAdoptionDate;
    }

    // Set isEstimatedTerminationDate
    if (data.isEstimatedTerminationDate !== undefined) {
      this.isEstimatedTerminationDate = !!data.isEstimatedTerminationDate;
    }

    // Set isEstimatedAmendedDate
    if (data.isEstimatedAmendedDate !== undefined) {
      this.isEstimatedAmendedDate = !!data.isEstimatedAmendedDate;
    }
  }

  private applyExisting(raw: string): void {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        if (
          parsed.modifiedFields &&
          typeof parsed.modifiedFields === "object"
        ) {
          const mapping: Record<string, keyof ModifiedFields> = {
            plan_action: "modifiedPlanAction",
            adoption_date: "modifiedAdoptionDate",
            effective_date: "modifiedEffectiveDate",
            amendment_date: "modifiedAmendmentDate",
            termination_date: "modifiedTerminationDate",
            expiration_date: "modifiedExpirationDate",
            number_of_shares: "modifiedNumberOfShares",
            transaction_type: "modifiedTransactionType",
            modifiedOther: "modifiedOther",
          };

          this.selectedFields = Object.entries(mapping)
            .filter(([_, prop]) => parsed.modifiedFields[prop])
            .map(([field]) => field);
        } else if (Array.isArray(parsed.fields)) {
          this.selectedFields = parsed.fields
            .map((entry: any) => entry?.field)
            .filter((field: any): field is string => typeof field === "string");
        } else if (typeof parsed.field === "string") {
          this.selectedFields = [parsed.field];
        }

        this.comment = parsed.comment || "";
        this.isEstimatedAdoptionDate = !!parsed.is_estimated_adoption_date;
        this.isEstimatedTerminationDate =
          !!parsed.is_estimated_termination_date;
        this.isEstimatedAmendedDate =
          !!parsed.is_estimated_amended_date;
        return;
      }
    } catch {
      // treat as legacy plain text
    }
    this.comment = raw ?? "";
  }
}
